/**
 * Deterministic failure harvesting for the AxAgent RLM loop.
 *
 * Builds a structured report of failure signals from the live action-log
 * entries at the end of a stage run — while the internal per-turn metadata
 * (`_functionCalls`) is still present — so run-end consumers (playbook
 * learning) never need the internal fields to survive state serialization.
 * Zero LLM calls; every heuristic here is pure and synchronous.
 */

import type { ActionLogEntry } from '../contextManager.js';
import { evaluateHindsight, extractErrorSignature } from '../contextManager.js';

export type AxAgentFailureSignalKind =
  /** A turn errored and was neither resolved nor a repeat of the prior turn. */
  | 'error_turn'
  /** A turn errored, a later turn succeeded, and the signature never recurred. */
  | 'resolved_error'
  /** A turn repeated the previous turn's failure (same error signature). */
  | 'dead_end'
  /** A registered tool/function call returned an error during a turn. */
  | 'tool_error';

export type AxAgentFailureSignal = {
  kind: AxAgentFailureSignalKind;
  /** Turn the failing action ran in. */
  turn: number;
  /** Normalized fingerprint (`extractErrorSignature`) used for deduping. */
  signature: string;
  /** Human-readable failure line (error message / `tool: error`). */
  detail: string;
  /** `resolved_error` only: turn whose action resolved the failure. */
  resolvedByTurn?: number;
  /** Truncated offending actor code (or tool arguments digest). */
  code?: string;
  /** Number of merged occurrences of this (kind, signature) pair. */
  occurrences: number;
};

export type AxAgentFailureReport = {
  stage: 'distiller' | 'executor';
  signals: readonly AxAgentFailureSignal[];
};

/** Playbook section that curated failure-avoidance rules land in. */
export const axPlaybookFailureSection = 'failures_to_avoid';

const MAX_SIGNAL_CODE_CHARS = 240;
const MAX_SIGNAL_DETAIL_CHARS = 200;
/**
 * Max distinct signals shown to the curator per update. The run-end learner
 * must cap the signatures it RECORDS as covered to the same set (only the
 * shown signals can produce a bullet), so this is exported.
 */
export const MAX_FEEDBACK_SIGNALS = 12;
const MAX_FEEDBACK_TASK_CHARS = 300;

function truncateInline(text: string, maxChars: number): string {
  const collapsed = text.replace(/\s+/g, ' ').trim();
  return collapsed.length > maxChars
    ? `${collapsed.slice(0, maxChars - 1)}…`
    : collapsed;
}

/**
 * First informative line of an error output: prefer a `XxxError: …` line that
 * is not a bracketed notice, then any non-bracketed non-ellipsis line, then
 * the raw signature. Mirrors the deterministic resolved-error tombstone
 * heuristic in `contextManager.ts`.
 */
function firstInformativeErrorLine(output: string): string {
  const lines = output
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  return (
    lines.find((line) => /\b\w+Error:/.test(line) && !line.startsWith('[')) ??
    lines.find((line) => !line.startsWith('[') && !line.startsWith('...')) ??
    extractErrorSignature(output)
  );
}

/**
 * Post-hoc hindsight over cloned minimal entries. Returns the set of turns
 * whose entry the shared `evaluateHindsight` heuristic tags `dead-end`. The
 * clones keep the source entries untouched (`evaluateHindsight` mutates its
 * arguments).
 */
function collectDeadEndTurns(entries: readonly ActionLogEntry[]): Set<number> {
  const clones: ActionLogEntry[] = entries.map((entry) => ({
    turn: entry.turn,
    code: entry.code,
    output: entry.output,
    tags: [...entry.tags],
    ...(entry.producedVars ? { producedVars: [...entry.producedVars] } : {}),
    ...(entry.stepKind ? { stepKind: entry.stepKind } : {}),
  }));
  for (let i = 0; i + 1 < clones.length; i++) {
    const prev = clones[i];
    const curr = clones[i + 1];
    if (prev && curr) {
      evaluateHindsight(prev, curr);
    }
  }
  return new Set(
    clones
      .filter((clone) => clone.tags.includes('dead-end'))
      .map((clone) => clone.turn)
  );
}

function mergeSignal(
  merged: Map<string, AxAgentFailureSignal>,
  signal: Omit<AxAgentFailureSignal, 'occurrences'>
): void {
  const key = `${signal.kind} ${signal.signature}`;
  const existing = merged.get(key);
  if (existing) {
    existing.occurrences += 1;
    return;
  }
  merged.set(key, { ...signal, occurrences: 1 });
}

/**
 * Build the failure report for one stage run. Every error-tagged turn yields
 * exactly one signal — `resolved_error` when a later non-error turn exists and
 * the signature never recurs, `dead_end` when the shared hindsight heuristic
 * marks it (repeat of the same signature), `error_turn` otherwise — plus one
 * `tool_error` signal per distinct failing runtime call. Signals sharing a
 * (kind, signature) pair merge into one entry with an occurrence count.
 * Returns `undefined` for clean runs.
 */
export function buildFailureReport(
  entries: readonly ActionLogEntry[],
  stage: 'distiller' | 'executor'
): AxAgentFailureReport | undefined {
  // Runs on every agent run (both stages), so it must never throw — a
  // malformed entry (e.g. a hand-restored state) would otherwise break an
  // otherwise-successful forward(). The learner treats undefined as "no
  // failures".
  try {
    return buildFailureReportUnsafe(entries, stage);
  } catch {
    return undefined;
  }
}

function buildFailureReportUnsafe(
  entries: readonly ActionLogEntry[],
  stage: 'distiller' | 'executor'
): AxAgentFailureReport | undefined {
  if (entries.length === 0) {
    return undefined;
  }

  const deadEndTurns = collectDeadEndTurns(entries);
  const merged = new Map<string, AxAgentFailureSignal>();

  entries.forEach((entry, index) => {
    if (entry.tags.includes('error')) {
      const signature = extractErrorSignature(entry.output);
      const detail = truncateInline(
        firstInformativeErrorLine(entry.output),
        MAX_SIGNAL_DETAIL_CHARS
      );
      const code = entry.code
        ? truncateInline(entry.code, MAX_SIGNAL_CODE_CHARS)
        : undefined;

      const laterEntries = entries.slice(index + 1);
      const resolver = laterEntries.find((e) => !e.tags.includes('error'));
      const recurs = laterEntries.some(
        (e) =>
          e.tags.includes('error') &&
          extractErrorSignature(e.output) === signature
      );

      if (resolver && !recurs) {
        const fix = resolver.summary ?? truncateInline(resolver.code, 120);
        mergeSignal(merged, {
          kind: 'resolved_error',
          turn: entry.turn,
          signature,
          detail: fix ? `${detail} — resolved by: ${fix}` : detail,
          resolvedByTurn: resolver.turn,
          ...(code ? { code } : {}),
        });
      } else if (deadEndTurns.has(entry.turn)) {
        mergeSignal(merged, {
          kind: 'dead_end',
          turn: entry.turn,
          signature,
          detail,
          ...(code ? { code } : {}),
        });
      } else {
        mergeSignal(merged, {
          kind: 'error_turn',
          turn: entry.turn,
          signature,
          detail,
          ...(code ? { code } : {}),
        });
      }
    }

    for (const call of entry._functionCalls ?? []) {
      if (!call.error) {
        continue;
      }
      const errorLine = truncateInline(call.error, 120);
      mergeSignal(merged, {
        kind: 'tool_error',
        turn: entry.turn,
        signature: `${call.qualifiedName}: ${errorLine.slice(0, 60)}`,
        detail: `${call.qualifiedName} failed: ${errorLine}`,
        ...(call.arguments !== undefined
          ? {
              code: truncateInline(
                safeStringify(call.arguments),
                MAX_SIGNAL_CODE_CHARS
              ),
            }
          : {}),
      });
    }
  });

  if (merged.size === 0) {
    return undefined;
  }
  return { stage, signals: [...merged.values()] };
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * Merge signals across stage reports, deduping by (kind, signature) and
 * summing occurrence counts. Order follows first appearance (distiller
 * signals before executor signals when both reports are passed in that
 * order).
 */
export function mergeFailureSignals(
  reports: readonly (AxAgentFailureReport | undefined)[]
): AxAgentFailureSignal[] {
  const merged = new Map<string, AxAgentFailureSignal>();
  for (const report of reports) {
    for (const signal of report?.signals ?? []) {
      const key = `${signal.kind} ${signal.signature}`;
      const existing = merged.get(key);
      if (existing) {
        existing.occurrences += signal.occurrences;
      } else {
        merged.set(key, { ...signal });
      }
    }
  }
  return [...merged.values()];
}

/**
 * Render the curator-facing failure digest. The steering nudges the curator
 * toward one precise avoidance rule per failure mode; run-to-run dedupe does
 * NOT depend on the curator honoring it (signatures are tracked
 * deterministically on the snapshot artifact by the coordinator).
 */
export function formatFailureFeedback(
  signals: readonly AxAgentFailureSignal[],
  task: string
): string {
  const shown = signals.slice(0, MAX_FEEDBACK_SIGNALS);
  const lines = shown.map((signal) => {
    const occurrences =
      signal.occurrences > 1 ? ` (x${signal.occurrences})` : '';
    const resolved =
      signal.resolvedByTurn !== undefined
        ? ` [resolved in turn ${signal.resolvedByTurn}]`
        : '';
    const code = signal.code ? `\n  failing code: ${signal.code}` : '';
    return `- (${signal.kind}${occurrences}) [${signal.signature}] ${signal.detail}${resolved}${code}`;
  });
  const omitted =
    signals.length > shown.length
      ? `\n(${signals.length - shown.length} more signals omitted)`
      : '';

  return `The agent run for task "${truncateInline(task, MAX_FEEDBACK_TASK_CHARS)}" produced these failure signals:

${lines.join('\n')}${omitted}

Curate durable avoidance rules into the playbook (suggested section: "${axPlaybookFailureSection}"):
- One concise avoidance or fix rule per failure mode, naming the concrete trigger (error type, tool, argument shape) so the rule is recognizable next time.
- UPDATE the existing bullet when one already covers the same failure mode instead of adding a near-duplicate.
- Phrase rules so they generalize to the failure mode, not this one task.
- Ignore signals that are one-off noise with no reusable lesson.`;
}
